import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress, IconButton, MenuItem, TextField, Typography, Dialog } from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Close as ClearIcon,
  AutoAwesome as AutoAwesomeIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "./AddressbookPanelShell";
import ContactCard from "../ContactCard";
import { useContactCategories } from "../useContactCategories";
import { useContactStatuses } from "../useContactStatuses";

const sx = {
  empty: { py: 4, display: "flex", justifyContent: "center" },
  cardList: { display: "flex", flexDirection: "column", gap: 1 },
  card: (selected) => (theme) => ({
    border: `1px solid ${selected ? "#3B82F6" : theme.palette.divider}`,
    borderRadius: 2,
    p: 1.5,
    backgroundColor: selected ? "#EFF6FF" : theme.palette.background.paper,
    borderLeft: "3px solid",
    borderLeftColor: selected ? "#3B82F6" : "divider",
    cursor: "pointer",
    "&:hover": { boxShadow: "0 1px 4px rgba(0,0,0,0.08)", backgroundColor: selected ? "#EFF6FF" : "#F9FAFB" },
    transition: "all 0.15s ease",
  }),
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cardName: { fontSize: 14, fontWeight: 700, color: "text.primary" },
  cardTitle: { fontSize: 12, color: "text.secondary", lineHeight: 1.3 },
  cardCompany: { fontSize: 12, color: "#3B82F6", fontWeight: 500 },
  contactRow: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    mt: 0.25,
  },
  contactIcon: { fontSize: 14, color: "#6B7280" },
  contactText: { fontSize: 12, color: "text.primary" },
  cardFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    mt: 1,
  },
  actionRow: { display: "flex", alignItems: "center", gap: 0.25 },
  pagerLabel: { color: "text.secondary", fontSize: 11, minWidth: 72, textAlign: "center" },
};

const EmptyState = ({ message }) => (
  <Box sx={sx.empty}>
    <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{message}</Typography>
  </Box>
);

const toTelHref = (phone) => {
  const raw = String(phone || "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/[^+\d]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
};

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua);
};


const ContactListPanel = ({
  loading,
  error,
  contacts,
  total,
  page,
  totalPages,
  canPrevious,
  canNext,
  onPrevious,
  onNext,
  onClose,
  onAdd,
  onImport,
  onBulkEmail,
  selectedId,
  onSelect,
  onCompose,
  onComposeText,
  activeFilters,
  categoryFilter,
  onCategoryFilterChange,
  categoryOptions,
  onStatusChange,
  onCategoryChange,
  onDeleteContact,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) => {
  const [qrOpen, setQrOpen] = useState(false);
  const [qrPhone, setQrPhone] = useState("");
  const authorId = useSelector((s) => s.auth.userId);
  const sharedCategories = useContactCategories(authorId);
  const sharedStatuses = useContactStatuses(authorId);
  const listRootRef = useRef(null);
  const loadMoreRequestedRef = useRef(false);

  const handlePhoneClick = (phone) => {
    const telHref = toTelHref(phone);
    if (!telHref) return;
    if (isMobileDevice()) {
      window.location.href = telHref;
      return;
    }
    setQrPhone(String(phone || ""));
    setQrOpen(true);
  };

  const qrValue = toTelHref(qrPhone);
  const qrImageUrl = qrValue
    ? `https://quickchart.io/qr?size=220&text=${encodeURIComponent(qrValue)}`
    : "";

  useEffect(() => {
    if (!loadingMore) {
      loadMoreRequestedRef.current = false;
    }
  }, [loadingMore]);

  useEffect(() => {
    if (!onLoadMore || !hasMore) return undefined;
    const root = listRootRef.current;
    const scroller = root?.parentElement;
    if (!scroller) return undefined;

    const thresholdPx = 560;
    let rafId = null;
    const maybeLoadMore = () => {
      if (loading || loadingMore || loadMoreRequestedRef.current || !hasMore) return;
      const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (remaining <= thresholdPx) {
        loadMoreRequestedRef.current = true;
        onLoadMore();
      }
    };
    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        maybeLoadMore();
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    maybeLoadMore();

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [hasMore, loading, loadingMore, onLoadMore]);

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
          {onCategoryFilterChange && (
            <TextField
              select
              size="small"
              value={categoryFilter || ""}
              onChange={(e) => onCategoryFilterChange(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) => {
                  if (!selected) return "All Contact Types";
                  return String(selected);
                },
              }}
              sx={{
                width: 160,
                "& .MuiOutlinedInput-root": { height: 26, fontSize: 12, backgroundColor: "#fff" },
                "& .MuiSelect-select": {
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>All Contact Types</MenuItem>
              {(Array.isArray(categoryOptions) ? categoryOptions : []).map((category) => (
                <MenuItem key={category} value={category} sx={{ fontSize: 12 }}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      }
      headerActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {onBulkEmail && (
            <IconButton
              size="small"
              onClick={onBulkEmail}
              title="Send email to contact type (AWS SES)"
              sx={{
                background: "linear-gradient(135deg, #7C3AED 0%, #3B82F6 100%)",
                "&:hover": { background: "linear-gradient(135deg, #6D28D9 0%, #2563EB 100%)" },
                width: 24, height: 24, p: 0,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 14, color: "#FFFFFF" }} />
            </IconButton>
          )}
          {onImport && (
            <IconButton
              size="small"
              onClick={onImport}
              title="Import contacts"
              sx={{ backgroundColor: "#3F51B5", "&:hover": { backgroundColor: "#3949AB" }, width: 24, height: 24, p: 0 }}
            >
              <UploadFileIcon sx={{ fontSize: 14, color: "#FFFFFF" }} />
            </IconButton>
          )}
          {onAdd && (
            <IconButton
              size="small"
              onClick={onAdd}
              title="Add contact"
              sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}
            >
              <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
            </IconButton>
          )}
        </Box>
      }
      collectionLabel="Contacts"
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {contacts.length} / {total} contacts
        </Typography>
      }
      footerRight={
        <>
          <IconButton size="small" onClick={onPrevious} disabled={!canPrevious} aria-label="Previous page">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography sx={sx.pagerLabel}>
            {page} / {totalPages}
          </Typography>
          <IconButton size="small" onClick={onNext} disabled={!canNext} aria-label="Next page">
            <ChevronRight fontSize="small" />
          </IconButton>
        </>
      }
    >
      {!loading && !error && contacts.length === 0 && Array.isArray(activeFilters) && activeFilters.length > 0 && (
        <Box
          sx={{
            mb: 1.25,
            px: 1,
            py: 0.75,
            borderRadius: 1.25,
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
          }}
        >
          <Typography sx={{ fontSize: 11, color: "text.secondary", lineHeight: 1.35 }}>
            Active filters: {activeFilters.join(" | ")}
          </Typography>
        </Box>
      )}

      {loading && contacts.length === 0 && (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center", gap: 1.25 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Loading contacts...</Typography>
        </Box>
      )}

      {!loading && error && <EmptyState message={error} />}
      {!loading && !error && contacts.length === 0 && (
        <EmptyState message="No contacts in this collection yet." />
      )}

      {!error && contacts.length > 0 && (
        <Box ref={listRootRef} sx={sx.cardList}>
          {contacts.map((contact, idx) => (
            <ContactCard
              key={contact.id || `contact-card-${idx}`}
              contact={contact}
              selected={selectedId === contact.id}
              categories={sharedCategories}
              statuses={sharedStatuses}
              onClick={() => onSelect?.(contact)}
              onPhoneClick={handlePhoneClick}
              onCompose={onCompose ? () => onCompose(contact) : undefined}
              onComposeText={onComposeText ? () => onComposeText(contact) : undefined}
              onCategoryChange={
                onCategoryChange
                  ? (c, cat) => onCategoryChange(c, cat?.value || cat?.id || cat?.name || "")
                  : undefined
              }
              onStatusChange={
                onStatusChange
                  ? (c, statusValue) => onStatusChange(c, statusValue)
                  : undefined
              }
              onDelete={onDeleteContact ? () => onDeleteContact(contact) : undefined}
            />
          ))}
          {hasMore && (
            <Box sx={{ py: 1.25, display: "flex", justifyContent: "center" }}>
              {loadingMore ? (
                <CircularProgress size={16} />
              ) : (
                <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
                  Scroll to load more
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2, textAlign: "center", position: "relative" }}>
          <IconButton
            size="small"
            onClick={() => setQrOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: "#64748B" }}
            title="Close"
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Call From Your Phone</Typography>
          {qrImageUrl && (
            <Box
              component="img"
              src={qrImageUrl}
              alt="Phone call QR code"
              sx={{ width: 220, height: 220, mx: "auto", display: "block", borderRadius: 1, border: "1px solid #E2E8F0" }}
            />
          )}
          <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>{qrPhone}</Typography>
        </Box>
      </Dialog>
    </AddressbookPanelShell>
  );
};

export default ContactListPanel;
